import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { SectionContainer, SectionTitle } from '../common/styles';
import Actions from '../common/actions';
import Filters from '../common/filters';
import Loader from '../common/loader';
import { getReaderEvents } from '../../../services/analytics/audience-service';
import useInvestorTypesStore from './investor-types-components/state-hook';
import Description from './investor-types-components/description';
import TypeGrid from './investor-types-components/type-grid';

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 56px;

  & > *:first-child {
    order: -1;
    /* The whole desktop view only supports two resolutions: XLarge(1024px~1365px) and XXLarge(1366px+) */
    flex-basis: ${props => (props.theme.isXXLarge ? '33%' : '24%')};
  }

  &:after {
    order: 0;
    height: 100%;
    content: "";
    width: 22px;
    display: block;
  }

  & > *:last-child {
    order: 1;
    /* The whole desktop view only supports two resolutions: XLarge(1024px~1365px) and XXLarge(1366px+) */
    flex-basis: ${props => (props.theme.isXXLarge ? '67%' : '76%')};
    margin-top: 12px;
  }
`;

export default function InvestorTypes({
  id, contributors, actualDaysAgo, investorTypes, cachedAnalyticsDataUrls,
}) {
  const cachedInvestorTypesDataUrl = cachedAnalyticsDataUrls.investorTypesDataUrl;
  const setContributors = useInvestorTypesStore(state => state.setContributors);
  const setRawEvents = useInvestorTypesStore(state => state.setRawEvents);
  const setDaysAgo = useInvestorTypesStore(state => state.setDaysAgo);
  const set = useInvestorTypesStore(state => state.set);
  const storeContribs = useInvestorTypesStore(state => state.contributors);
  const daysAgo = useInvestorTypesStore(state => state.daysAgo);
  const generatedAt = useInvestorTypesStore(state => state.generatedAt);
  const requestData = useInvestorTypesStore(state => state.requestData);
  const storeLoading = useInvestorTypesStore(state => state.loading);
  const [loading, setLoading] = React.useState(false);
  const [inited, setInited] = React.useState(false);
  const [token, setToken] = React.useState(null);

  const fetch = React.useCallback(fromCache => {
    if (!_.get(contributors, 'length')) {
      return;
    }

    setLoading(true);
    const [task, source] = getReaderEvents(
      id,
      contributors.map(c => c.id),
      actualDaysAgo,
      cachedInvestorTypesDataUrl,
      fromCache,
    );
    setToken(source);
    task
      .then(response => {
        const payload = _.get(response, 'data', []);
        if (payload.length) {
          setInited(true);
          setRawEvents(payload);
          set(state => {
            state.generatedAt = moment();
          });
        }
      })
      .finally(() => {
        set(state => {
          state.requestData = false;
        });
        setLoading(false);
        setToken(null);
      });
  }, [contributors, actualDaysAgo, id, set, setRawEvents, cachedInvestorTypesDataUrl]);

  React.useEffect(() => {
    if (!inited) {
      setContributors(_.map(contributors, c => ({ ...c, selected: true })));
      fetch(true);
    }
  }, [inited, contributors, fetch, setContributors]);

  React.useEffect(() => {
    requestData && fetch(false);
  }, [requestData, fetch]);

  React.useEffect(() => {
    let tid;
    if (loading) {
      tid = setTimeout(() => set(state => {
        state.loading = true;
      }), 200);
    } else {
      set(state => {
        state.loading = false;
      });
    }
    return () => clearTimeout(tid);
  }, [loading, set]);

  const onCancel = () => token && token.cancel();

  const filtersProps = {
    daysAgo,
    contributors: storeContribs,
    onContributorsChange: setContributors,
    onDateChange: setDaysAgo,
  };

  const actionsProps = {
    generatedAt,
    onRefreshClick: () => set(state => {
      state.requestData = true;
    }),
  };

  return (
    <SectionContainer>
      {!!storeLoading && <Loader cancellable={inited} onCancel={onCancel} />}
      <Filters {...filtersProps} />
      <Actions {...actionsProps} />
      <SectionTitle>investor type breakdown</SectionTitle>
      <Wrapper>
        <Description />
        <TypeGrid {...{ investorTypes }} />
      </Wrapper>
    </SectionContainer>
  );
}

InvestorTypes.propTypes = {
  id: PropTypes.number.isRequired,
  cachedAnalyticsDataUrls: PropTypes.shape({
    investorTypesDataUrl: PropTypes.string,
  }).isRequired,
  contributors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    profilePictureUrl: PropTypes.string,
    createdAt: PropTypes.string,
  })).isRequired,
  actualDaysAgo: PropTypes.number.isRequired,
  investorTypes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
};
